<script>
   /*************************************************************/
   /* AppPlots component for test tab                          */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import plot components
   import PlotAcceptance from '../shared/PlotAcceptance.svelte';
   import PlotDataset from '../shared/PlotDataset.svelte';

   // import plot controls components
   import { CAT_COLORS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { getPlotAcceptanceControls, getPlotExtremesControls } from '@mdatools-apps/shared/plots/Controls.svelte.js';

   let {
      nimca,
      testres,
      testsetp,
      markOutlier,
   } = $props();


   // the data and the results this component was mounted with. 'untrack' marks the capture as
   // deliberate: AppContainer wraps this component in a key block, so a new set of results arrives
   // as a new component and never as new props for this one
   const data = untrack(() => ({'test': testsetp}));
   const res = untrack(() => ({'test': testres}));

   // colors for acceptance plot
   const classColmapMembers = {'regular' : CAT_COLORS.regular, 'extreme': CAT_COLORS.extreme, 'outlier': CAT_COLORS.outlier};
   const classColmapStrangers = {'alien' : CAT_COLORS.alien, 'external': CAT_COLORS.external};

   // options for acceptance plot
   const acceptanceOptions = $derived.by(() => {
      const res = testres.outcomes;
      return {
         'members' : {
            classColmap: classColmapMembers,
            labels: [`regular: ${res.rolsNum.regular}`, `extreme: ${res.rolsNum.extreme}`, `outliers: ${res.rolsNum.outlier}`],
            values: res.rols,
            ind: testres.ind.members
         },
         'strangers': {
            classColmap: classColmapStrangers,
            labels: [`aliens: ${res.rolsNum.alien}`, `external: ${res.rolsNum.external}`],
            values: res.rols,
            ind: testres.ind.strangers
         }
      }
   });


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons},
      dataset: {label: 'Datasets', scope: 'objects', component: PlotDataset, buttons: buttons},
   })

   // JSON with plots controls (as Widget properties). a plain object: nothing here sets a
   // 'hidden' flag or touches a descriptor after it is made, so there is nothing for $state
   // to track - only 'params' below changes
   const controls = untrack(() => ({
      acceptance: getPlotAcceptanceControls(res),
      dataset: getPlotExtremesControls(['test']),
   }))

   // JSON with currently selected plots parameters
   let params = $state({
      acceptance: getDefaults(controls.acceptance),
      dataset: getDefaults(controls.dataset),
   });

   // if limType or result objects changed make updates to acceptance plot fixed parameters
   $effect(() => {
      if (acceptanceOptions && params.acceptance.subset) {
         params.acceptance['colorByObj'] = params.acceptance.subset !== 'all' ? acceptanceOptions[params.acceptance.subset] : null;
      }
   });

   // the subset selector owns 'colorBy' only while it is on screen. when there are no members it is
   // hidden and a 'color by' selector takes its place, so without this guard every choice made in
   // that selector was overwritten here on the next run
   $effect(() => {
      if (!controls.acceptance.subset.hidden) {
         params.acceptance['colorBy'] = params.acceptance.subset !== 'all' ? 'roles' : 'classes';
      }
   });


   // combine main elements to be shared with all plots together
   const props = $derived({ res, model: nimca, data});

</script>

{#snippet buttons(sel)}
   <Container label="Selected">
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}


<PlotsRouter {plots} {controls} {props} bind:params={params} />